import clsx from "clsx";
import { Check } from "lucide-react";
import { Text } from "~/components/Text";

type StepNumber = 1 | 2 | 3;

type StepIndicatorProps = {
  currentStep: StepNumber;
  showTicketStep?: boolean;
  steps?: readonly { number: StepNumber; label: string }[];
  stepActions?: Partial<Record<StepNumber, () => void>>;
};

const LEGACY_STEPS = [
  { number: 1, label: "Checkout" },
  { number: 2, label: "Payment" },
] as const;

const TICKET_STEPS = [
  { number: 1, label: "Tickets" },
  { number: 2, label: "Info" },
  { number: 3, label: "Payment" },
] as const;

export function StepIndicator({
  currentStep,
  showTicketStep = false,
  steps = showTicketStep ? TICKET_STEPS : LEGACY_STEPS,
  stepActions,
}: StepIndicatorProps) {
  return (
    <div className="flex items-center gap-1 sm:gap-3">
      {steps.map((step, index) => {
        const isActive = step.number === currentStep;
        const isComplete = step.number < currentStep;
        const isReached = isActive || isComplete;
        const action = isComplete ? stepActions?.[step.number] : undefined;
        const content = (
          <>
            <span
              className={clsx(
                "flex h-[27px] w-[27px] items-center justify-center rounded-lg text-sm font-bold leading-none transition-colors",
                isReached
                  ? "bg-butter-pink text-butter-cream-light"
                  : "bg-butter-cream text-butter-green",
              )}
            >
              {isComplete ? (
                <Check className="size-4" strokeWidth={3} aria-hidden />
              ) : (
                <span className="-mt-px">{step.number}</span>
              )}
            </span>
            <Text
              as="span"
              variant="secondary"
              size="sm"
              weight={isReached ? "bold" : "regular"}
              className={clsx(
                action && "transition-colors group-hover:text-butter-pink",
              )}
            >
              {step.label}
            </Text>
          </>
        );

        return (
          <div key={step.number} className="flex items-center gap-1 sm:gap-3">
            {action ? (
              <button
                type="button"
                onClick={action}
                className="group -mx-1.5 -my-1 flex cursor-pointer items-center gap-1.5 rounded-lg px-1.5 py-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-butter-green sm:gap-2"
                aria-label={`Go back to ${step.label}`}
              >
                {content}
              </button>
            ) : (
              <div className="flex items-center gap-1.5 sm:gap-2">{content}</div>
            )}
            {index < steps.length - 1 && (
              <span
                className={clsx(
                  "h-px w-4 transition-colors sm:w-8",
                  isComplete ? "bg-butter-pink" : "bg-butter-green/20",
                )}
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
